/**
 * cart-store.js — Alpine.store('cart') with removed-items undo bucket.
 *
 * Active cart: items[] — used for checkout totals and order payload.
 * Removed bucket: removedItems[] — distinct item types (badge = removedItems.length).
 * Remove moves line to bucket; Undo restores full quantity back to cart.
 */
document.addEventListener('alpine:init', () => {
  const cfg = window._webMenuConfig || {};
  const cartKey = cfg.storageKey || ('cart-' + (cfg.pageId || 'default'));
  const removedKey = cfg.removedStorageKey || ('removed-' + (cfg.pageId || 'default'));
  const specialInstructionsKey = cfg.specialInstructionsStorageKey || ('special-instructions-' + (cfg.pageId || 'default'));
  const storage = window.WebMenuStorage.create(cfg.pageId || 'webmenu');

  function loadLines(key) {
    try {
      const raw = storage.get(key);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  function loadText(key) {
    try {
      return storage.get(key) || '';
    } catch {
      return '';
    }
  }

  function notify() {
    window.dispatchEvent(new CustomEvent('cart-updated'));
  }

  function normalizeQuantity(value) {
    const qty = Math.floor(Number(value));
    return Number.isFinite(qty) ? qty : 0;
  }

  function roundMoney(amount) {
    return Math.round((Number(amount) + Number.EPSILON) * 100) / 100;
  }

  function cloneLine(item) {
    return {
      id: item.id,
      name: item.name,
      price: roundMoney(item.price),
      image: item.image || '',
      quantity: Math.max(1, normalizeQuantity(item.quantity))
    };
  }

  /** Drop invalid persisted lines (qty < 1). */
  function sanitizeLines(arr) {
    return (Array.isArray(arr) ? arr : [])
      .map(item => ({
        id: item.id,
        name: item.name,
        price: roundMoney(item.price),
        image: item.image || '',
        quantity: normalizeQuantity(item.quantity)
      }))
      .filter(item => item.name && item.quantity >= 1);
  }

  Alpine.store('cart', {
    items: sanitizeLines(loadLines(cartKey)),
    removedItems: sanitizeLines(loadLines(removedKey)),
    specialInstructions: loadText(specialInstructionsKey),
    storageBackend: storage.backend,

    get count() {
      return this.items.reduce((sum, i) => sum + i.quantity, 0);
    },

    get total() {
      return roundMoney(this.items.reduce((sum, i) => sum + i.price * i.quantity, 0));
    },

    /** Distinct removed item types (not sum of quantities). */
    get removedCount() {
      return this.removedItems.length;
    },

    _sanitize() {
      this.items = sanitizeLines(this.items);
      this.removedItems = sanitizeLines(this.removedItems);
    },

    persist() {
      this._sanitize();
      storage.set(cartKey, JSON.stringify(this.items));
      storage.set(removedKey, JSON.stringify(this.removedItems));
      storage.set(specialInstructionsKey, this.specialInstructions || '');
      notify();
    },

    setSpecialInstructions(text) {
      this.specialInstructions = String(text || '');
      storage.set(specialInstructionsKey, this.specialInstructions);
      notify();
    },

    _moveToRemoved(item) {
      const qty = Math.max(1, normalizeQuantity(item.quantity));
      if (qty < 1) return;

      const line = cloneLine({ ...item, quantity: qty });
      const key = line.id || line.name;
      const existing = this.removedItems.find(r => (r.id || r.name) === key);
      if (existing) {
        existing.quantity += line.quantity;
        if (!existing.image && line.image) existing.image = line.image;
      } else {
        this.removedItems.push(line);
      }
    },

    _mergeIntoCart(item) {
      const line = cloneLine(item);
      const key = line.id || line.name;
      const existing = this.items.find(i => (i.id || i.name) === key);
      if (existing) {
        existing.quantity += line.quantity;
        if (!existing.image && line.image) existing.image = line.image;
      } else {
        this.items.push(line);
      }
    },

    add(item) {
      const key = item.id || item.name;
      const img = item.image || item.imageUrl || '';
      const existing = this.items.find(i => (i.id || i.name) === key);
      if (existing) {
        existing.quantity += 1;
        if (!existing.image && img) existing.image = img;
      } else {
        this.items.push({
          id: item.id || item.name,
          name: item.name,
          price: roundMoney(item.price),
          image: img,
          quantity: 1
        });
      }
      this.persist();
    },

    /** Remove line from active cart → removed bucket. */
    remove(index) {
      const item = this.items[index];
      if (!item) return;
      this._moveToRemoved(item);
      this.items.splice(index, 1);
      this.persist();
    },

    updateQuantity(index, delta) {
      const item = this.items[index];
      if (!item) return;

      if (delta < 0) {
        if (item.quantity <= 1) {
          this._moveToRemoved(item);
          this.items.splice(index, 1);
        } else {
          item.quantity -= 1;
        }
      } else if (delta > 0) {
        item.quantity += delta;
      }
      this.persist();
    },

    updateQuantityByName(name, delta) {
      const index = this.items.findIndex(i => i.name === name);
      if (index === -1) return;
      this.updateQuantity(index, delta);
    },

    getItemQuantity(name) {
      const item = this.items.find(i => i.name === name);
      return item ? item.quantity : 0;
    },

    /** Restore a removed line back into the active cart. */
    restoreRemoved(index) {
      const item = this.removedItems[index];
      if (!item || normalizeQuantity(item.quantity) < 1) {
        if (item) this.removedItems.splice(index, 1);
        this.persist();
        return;
      }
      this._mergeIntoCart(item);
      this.removedItems.splice(index, 1);
      this.persist();
    },

    clear() {
      this.items = [];
      this.removedItems = [];
      this.specialInstructions = '';
      storage.remove(cartKey);
      storage.remove(removedKey);
      storage.remove(specialInstructionsKey);
      notify();
    }
  });

  Alpine.store('cart').persist();
});
